import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './Reports.css';

// Función para traducir estados de pago
const translatePaymentStatus = (status) => {
  const translations = {
    'pending': 'Pendiente',
    'paid': 'Pagado',
    'partial': 'Parcial',
    'overdue': 'Vencido',
    'cancelled': 'Cancelado'
  };
  return translations[status] || status;
};

const ReportFinancial = () => {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: ''
  });
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await api.get('/payments', { params });
      setPreviewData(response.data || []);
    } catch (error) {
      console.error('Error al cargar vista previa:', error);
      toast.error('Error al cargar vista previa');
      setPreviewData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadPreview();
    setCurrentPage(1);
  }, [loadPreview]);

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      
      if (filters.dateFrom) queryParams.append('date_from', filters.dateFrom);
      if (filters.dateTo) queryParams.append('date_to', filters.dateTo);
      
      const baseUrl = api.defaults.baseURL || 'http://localhost:8000/api/v1';
      const url = `${baseUrl}/reports/financial/excel${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar el reporte');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_financiero_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Reporte descargado correctamente');
    } catch (error) {
      console.error('Error al descargar:', error);
      toast.error('Error al descargar el reporte');
    }
  };

  const calculateStats = () => {
    const totalRevenue = previewData.reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0);
    const pendingRevenue = previewData
      .filter(p => p.status === 'pending' || p.status === 'partial')
      .reduce((sum, p) => sum + (parseFloat(p.amount) - (parseFloat(p.paid_amount) || 0)), 0);
    const overdueRevenue = previewData
      .filter(p => p.status === 'overdue')
      .reduce((sum, p) => sum + (parseFloat(p.amount) - (parseFloat(p.paid_amount) || 0)), 0);

    return { totalRevenue, pendingRevenue, overdueRevenue };
  };

  const stats = calculateStats();

  return (
    <div className="reports-page">
      <div className="page-header">
        <h2>📈 Reporte Financiero</h2>
        <p>Resumen financiero con ingresos, morosidad y estadísticas</p>
      </div>

      {/* Filtros */}
      <div className="card filters-section">
        <h3>Filtros (Opcionales)</h3>
        <div className="filters-grid">
          <div className="form-group">
            <label>Fecha Desde</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Fecha Hasta</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Vista previa */}
      <div className="card preview-section">
        <div className="preview-header">
          <h3>Resumen Financiero</h3>
          <div className="export-buttons">
            <button 
              className="btn btn-primary"
              onClick={handleDownload}
            >
              📊 Exportar a Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="preview-loading">
            <p>Cargando datos...</p>
          </div>
        ) : (
          <div className="financial-summary">
            <div className="financial-stats">
              <div className="stat-card">
                <h4>Total Recaudado</h4>
                <p className="stat-value">S/ {stats.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="stat-card">
                <h4>Pendiente de Cobro</h4>
                <p className="stat-value">S/ {stats.pendingRevenue.toFixed(2)}</p>
              </div>
              <div className="stat-card">
                <h4>Vencido (Morosidad)</h4>
                <p className="stat-value text-danger">S/ {stats.overdueRevenue.toFixed(2)}</p>
              </div>
              <div className="stat-card">
                <h4>Total de Pagos</h4>
                <p className="stat-value">{previewData.length}</p>
              </div>
            </div>
            
            <div className="preview-table-container" style={{ marginTop: '20px' }}>
              <h4>Últimos Pagos</h4>
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Estudiante</th>
                    <th>Fecha</th>
                    <th>Monto</th>
                    <th>Pagado</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(payment => (
                    <tr key={payment.id}>
                      <td>{payment.student?.full_name || 'Sin estudiante'}</td>
                      <td>{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : ''}</td>
                      <td>S/ {payment.amount}</td>
                      <td>S/ {payment.paid_amount || 0}</td>
                      <td>
                        <span className={`status-badge status-${payment.status}`}>
                          {translatePaymentStatus(payment.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {previewData.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, previewData.length)} - {Math.min(currentPage * itemsPerPage, previewData.length)} de {previewData.length} registros
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      « Primera
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      ‹ Anterior
                    </button>
                    <span className="pagination-current">
                      Página {currentPage} de {Math.ceil(previewData.length / itemsPerPage)}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(previewData.length / itemsPerPage)))}
                      disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}
                    >
                      Siguiente ›
                    </button>
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(Math.ceil(previewData.length / itemsPerPage))}
                      disabled={currentPage >= Math.ceil(previewData.length / itemsPerPage)}
                    >
                      Última »
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportFinancial;
